import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import axios from "axios";
import liff from "@line/liff";
import Header from "../components/common/Header";
import BottomNav from "../components/common/BottomNav";
import { ensureLiffInit } from "../liffClient";
import { colors, fontFamily } from "../theme/palette";

type Mistake = {
  word: string;
  image_url: string;
  meaning?: string;
  lang?: string;
  attempted_at: string;
};

// SpeechSynthesis に渡す BCP-47 の言語コード
const SPEECH_LANG: Record<string, string> = {
  english: "en-US",
  french: "fr-FR",
  japanese: "ja-JP",
};

const speak = (word: string, lang?: string) => {
  const instance = new SpeechSynthesisUtterance(word);
  instance.lang = SPEECH_LANG[lang ?? ""] ?? "en-US";
  window.speechSynthesis.speak(instance);
};

export default function QuizMistakes() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [loginFailed, setLoginFailed] = useState(false);
  const [mistakes, setMistakes] = useState<Mistake[] | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      const ready = await ensureLiffInit();
      const isLoggedIn = ready && liff.isLoggedIn();
      setLoggedIn(isLoggedIn);
      if (!isLoggedIn) return;

      try {
        const profile = await liff.getProfile();
        setUserId(profile.userId);
        const response = await axios.post("/v1/api/quiz/mistakes/fetch", {
          userId: profile.userId,
        });
        setMistakes([...(response.data.mistakes ?? [])].reverse());
      } catch (error) {
        console.error("Error fetching quiz mistakes:", error);
      }
    };

    load();
  }, []);

  const handleDelete = async (attemptedAt: string) => {
    if (!userId) return;
    try {
      await axios.post("/v1/api/quiz/mistake/delete", {
        userId,
        attemptedAt,
      });
      setMistakes((prev) => (prev ?? []).filter((m) => m.attempted_at !== attemptedAt));
    } catch (error) {
      console.error("Error deleting quiz mistake:", error);
    }
  };

  const handleLogin = async () => {
    const ok = await ensureLiffInit();
    if (!ok) {
      setLoginFailed(true);
      return;
    }
    liff.login();
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: colors.bg, display: "flex", flexDirection: "column" }}>
      <Box sx={{ width: "100%", position: "sticky", top: "0", zIndex: 100 }}>
        <Header />
      </Box>

      <Box sx={{ maxWidth: 560, mx: "auto", px: 2, pt: 4, pb: 2, flex: 1, width: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 4 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "13px",
              background: colors.accent,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <InsightsRoundedIcon sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1.4rem", color: colors.textPrimary, lineHeight: 1.2, fontFamily }}>
              間違えた問題
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: colors.textMuted, fontFamily }}>
              クイズで間違えた単語の一覧
            </Typography>
          </Box>
        </Box>

        {!loggedIn && (
          <Box
            sx={{
              backgroundColor: colors.card,
              borderRadius: "18px",
              p: 3,
              textAlign: "center",
              boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
              border: `1px solid ${colors.border}`,
            }}
          >
            <Typography sx={{ color: colors.textSecondary, fontSize: "0.9rem", mb: 2, fontFamily }}>
              ログインすると間違えた問題を確認できます
            </Typography>
            <Button
              onClick={handleLogin}
              variant="contained"
              sx={{
                background: colors.accent,
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                fontFamily,
                px: 3,
                "&:hover": { background: colors.accentDark },
              }}
            >
              ログイン
            </Button>
            {loginFailed && (
              <Typography sx={{ color: colors.danger, fontSize: "0.78rem", mt: 1.5, fontFamily }}>
                ログインに接続できませんでした。時間をおいて再度お試しください。
              </Typography>
            )}
          </Box>
        )}

        {loggedIn && mistakes && mistakes.length === 0 && (
          <Typography sx={{ color: colors.textMuted, textAlign: "center", mt: 4, fontFamily }}>
            間違えた問題はまだありません
          </Typography>
        )}

        {loggedIn &&
          mistakes?.map((item, i) => (
            <Box
              key={i}
              sx={{
                backgroundColor: colors.card,
                borderRadius: "18px",
                mb: 2.5,
                boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
                border: `1px solid ${colors.border}`,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                p: 2,
                gap: 1.5,
              }}
            >
              {item.image_url && (
                <Box
                  component="img"
                  src={item.image_url}
                  alt={item.word}
                  sx={{
                    width: 72,
                    height: 72,
                    objectFit: "cover",
                    borderRadius: "12px",
                    flexShrink: 0,
                    border: `2px solid ${colors.bg}`,
                  }}
                />
              )}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: colors.textPrimary, wordBreak: "break-word", fontFamily }}>
                  {item.word}
                </Typography>
                {item.meaning && (
                  <Typography sx={{ fontSize: "0.88rem", color: colors.textSecondary, fontWeight: 500, mt: 0.25, wordBreak: "break-word", fontFamily }}>
                    {item.meaning}
                  </Typography>
                )}
                <Typography sx={{ fontSize: "0.78rem", color: colors.textMuted, mt: 0.5, fontFamily }}>
                  {new Date(item.attempted_at).toLocaleString("ja-JP")}
                </Typography>
              </Box>

              <IconButton
                onClick={() => speak(item.word, item.lang)}
                sx={{
                  width: 40,
                  height: 40,
                  background: colors.accent,
                  color: "#fff",
                  flexShrink: 0,
                  "&:hover": { background: colors.accentDark },
                }}
              >
                <VolumeUpRoundedIcon sx={{ fontSize: 20 }} />
              </IconButton>

              <IconButton
                onClick={() => handleDelete(item.attempted_at)}
                sx={{
                  width: 36,
                  height: 36,
                  color: colors.textFaint,
                  flexShrink: 0,
                  "&:hover": { color: colors.danger, backgroundColor: colors.dangerSoft },
                  transition: "color 0.15s, background-color 0.15s",
                }}
              >
                <DeleteOutlineRoundedIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          ))}
      </Box>

      <BottomNav />
    </Box>
  );
}
